import { MaterialIcons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useEffect, useState } from 'react';
import { FlatList, Pressable, StyleSheet, useWindowDimensions } from 'react-native';
import { Modal, Portal } from 'react-native-paper';
import Animated, {
  FadeInUp,
  FadeOutUp,
  useAnimatedRef,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming
} from 'react-native-reanimated';

import { firebaseImage, ThumbSize } from '@/utils/firebaseImage';

interface GalleryProps {
  thumbnails: string[];
  initialImage: number;
  showGallery: boolean;
  hideGallery: () => void;
}

const AnimatedBtn = Animated.createAnimatedComponent(Pressable);

const Gallery = ({ thumbnails, initialImage, showGallery, hideGallery }: GalleryProps) => {
  const { width, height } = useWindowDimensions();

  const imgSize = (width > height ? height : width) - 100;

  const currentIndex = useSharedValue<number>(0);
  const flatlistOpacity = useSharedValue<number>(0);
  const carouselRef = useAnimatedRef<FlatList<string>>();

  const [disabledButton, setDisabledButton] = useState<'left' | 'none' | 'right'>(
    currentIndex.value === 0 ? 'left' : currentIndex.value === thumbnails.length - 1 ? 'right' : 'none'
  );

  useEffect(() => {
    flatlistOpacity.value = withTiming(1, {
      duration: 300
    });
  });
  useDerivedValue(() => {
    carouselRef.current?.scrollToOffset({
      animated: true,
      offset: currentIndex.value * imgSize
    });
    if (currentIndex.value === 0) {
      setDisabledButton('left');
    } else if (currentIndex.value === thumbnails.length - 1) {
      setDisabledButton('right');
    } else setDisabledButton('none');
  });

  //Left button
  const btnLeftAnimatedStyle = useAnimatedStyle(() => ({
    opacity: currentIndex.value === 0 ? 0 : 1
  }));

  //Right button
  const btnRightAnimatedStyle = useAnimatedStyle(() => ({
    opacity: currentIndex.value === thumbnails.length - 1 ? 0 : 1
  }));

  // Animated style for the FlatList container
  const modalAnimatedStyle = useAnimatedStyle(() => ({
    opacity: flatlistOpacity.value
  }));

  return (
    <Portal>
      <Modal
        theme={{
          animation: {
            scale: 0
          }
        }}
        style={styles.modal}
        contentContainerStyle={styles.contentModal}
        visible={showGallery}
        onDismiss={() => {
          flatlistOpacity.value = withTiming(0, { duration: 300 }, hideGallery);
        }}>
        <Animated.View
          style={[{ width: imgSize, height: imgSize }, modalAnimatedStyle]}
          entering={FadeInUp}
          exiting={FadeOutUp}>
          <Animated.FlatList
            ref={(node: FlatList<string> | null) => {
              if (node) {
                carouselRef.current = node;
                currentIndex.value = initialImage;
                node.scrollToOffset({
                  animated: true,
                  offset: currentIndex.value * imgSize
                });
              }
            }}
            data={thumbnails}
            renderItem={({ item, index }: { item: string; index: number }) => (
              <Image
                cachePolicy={'memory-disk'}
                source={firebaseImage(thumbnails.at(index) ?? '', ThumbSize.Large)}
                style={[
                  styles.imageGallery,
                  {
                    width: imgSize,
                    height: imgSize
                  }
                ]}
                contentFit="cover"
              />
            )}
            snapToInterval={imgSize}
            horizontal
            showsHorizontalScrollIndicator={false}
            keyExtractor={(item: string) => item}
          />
          <div
            className="w-full h-auto absolute flex flex-row justify-between px-4"
            style={{ bottom: imgSize / 2 - 30 }}>
            <AnimatedBtn
              disabled={disabledButton === 'left'}
              style={[styles.controlsBtn, btnLeftAnimatedStyle]}
              onPress={() => currentIndex.value > 0 && (currentIndex.value -= 1)}>
              <MaterialIcons name="chevron-left" size={60} color="black" />
            </AnimatedBtn>
            <AnimatedBtn
              disabled={disabledButton === 'right'}
              style={[styles.controlsBtn, btnRightAnimatedStyle]}
              onPress={() => currentIndex.value < thumbnails.length - 1 && (currentIndex.value += 1)}>
              <MaterialIcons name="chevron-right" size={60} color="black" />
            </AnimatedBtn>
          </div>
        </Animated.View>
      </Modal>
    </Portal>
  );
};

export default Gallery;

const styles = StyleSheet.create({
  modal: {
    alignItems: 'center',
    paddingHorizontal: 80
  },
  contentModal: {
    borderRadius: 12,
    overflow: 'hidden',
    maxWidth: 1152,
    flexDirection: 'column'
  },
  imageGallery: {
    backgroundColor: 'white'
  },
  controlsBtn: {
    backgroundColor: 'white',
    borderRadius: 30
  }
});
